import React, { useState } from "react";
import { api, openPdf } from "@finance/lib/api";
import { Button } from "@finance/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@finance/components/ui/dialog";

// Maps a pdf endpoint path to a canonical document type so the right
// disclaimer(s) can be pre-selected for that document.
const DOC_TYPE_BY_PREFIX = {
  "invoice": "invoice",
  "client-invoice": "invoice",
  "delivery-note": "delivery_note",
  "delivery-note-doc": "delivery_note",
  "grv": "grv",
  "goods-return": "goods_return",
  "credit-note": "credit_note",
  "debit-note": "debit_note",
  "expense-po": "purchase_order",
  "purchase-order": "purchase_order",
  "payment-voucher": "payment_voucher",
  "transaction-card": "transaction_card",
};

function docTypeFromPath(path) {
  const parts = (path || "").split("/");
  const idx = parts.indexOf("pdf");
  const prefix = idx >= 0 ? parts[idx + 1] : parts[1];
  return DOC_TYPE_BY_PREFIX[prefix] || null;
}

// Trigger that lets the user pick disclaimers (from the editable list) before opening/downloading the PDF.
export function PrintButton({ path, children, className, testid }) {
  const [open, setOpen] = useState(false);
  const [discs, setDiscs] = useState([]);
  const [sel, setSel] = useState([]);

  const dt = docTypeFromPath(path);

  const openDlg = async () => {
    let data = [];
    try { ({ data } = await api.get("/disclaimers", { params: { active_only: true } })); }
    catch { data = []; }
    setDiscs(data);
    // Pre-select disclaimers tagged for this document type.
    setSel(dt ? data.filter((d) => (d.doc_types || []).includes(dt)).map((d) => d.id) : []);
    setOpen(true);
  };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const go = () => {
    const q = sel.length ? `?disclaimer_ids=${sel.join(",")}` : "";
    openPdf(`${path}${q}`);
    setOpen(false);
  };

  return (
    <>
      <button type="button" className={className} data-testid={testid} onClick={openDlg}>{children}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="print-disclaimer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Include disclaimers?</DialogTitle>
            <DialogDescription>Select any disclaimers/terms to print on this document. Suggested ones for this document are pre-ticked.</DialogDescription>
          </DialogHeader>
          {discs.length === 0 && <p className="text-sm text-gray-400">No disclaimers configured yet. Add them on the Disclaimers page.</p>}
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {discs.map((d) => {
              const suggested = dt && (d.doc_types || []).includes(dt);
              return (
                <label key={d.id} className="flex items-start gap-2 text-sm" data-testid={`print-disc-${d.id}`}>
                  <input type="checkbox" className="mt-1" checked={sel.includes(d.id)} onChange={() => toggle(d.id)} />
                  <span>
                    <span className="font-medium">{d.title}</span>
                    {suggested && <span className="ml-2 rounded-sm bg-[#E6ECF5] px-1.5 py-0.5 text-[10px] font-semibold text-[#122E5C]">Suggested</span>}
                    <span className="block text-xs text-gray-500">{d.body}</span>
                  </span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="print-confirm" onClick={go}>Download PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
